/**
 * Authentication hooks for React components
 *
 * Provides easy-to-use hooks for authentication state and operations:
 * - useAuth() - Get current session and user
 * - useSession() - Get session data with loading state
 * - useUser() - Get current user or null
 *
 * Example usage:
 *   const { user, isLoading } = useAuth();
 *   if (isLoading) return <Spinner />;
 *   if (!user) return <Login />;
 *   return <Dashboard user={user} />;
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { authClient, type User } from "@/lib/auth-client";

const BETTER_AUTH_SESSION_QUERY_KEY = ["better-auth", "session"] as const;
const GATEWAY_AUTH_QUERY_KEY = ["auth"] as const;

/**
 * Hook to get current authentication session
 * Returns session data with loading and error states
 */
export function useSession() {
  return useQuery({
    queryKey: BETTER_AUTH_SESSION_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await authClient.getSession();
      if (error) {
        throw new Error(error.message || "Failed to get session");
      }
      return data;
    },
    // Refetch session on window focus to detect sign-outs in other tabs
    refetchOnWindowFocus: true,
    // Keep session data fresh
    staleTime: 30 * 1000, // 30 seconds
    // Retry failed requests (network issues, etc.)
    retry: 1,
  });
}

/**
 * Hook to get current authenticated user
 * Returns user object or null if not authenticated
 */
export function useUser(): User | null {
  const { data } = useSession();
  return data?.user ?? null;
}

/**
 * Main authentication hook
 * Combines session and user data with convenient helpers
 */
export function useAuth() {
  const { data: session, isLoading, error, refetch } = useSession();
  const queryClient = useQueryClient();

  return {
    // Session data
    session,
    user: session?.user ?? null,

    // Loading states
    isLoading,
    isAuthenticated: !!session?.user,

    // Error handling
    error,

    // Refetch session (useful after sign in/out)
    refetch,

    // Convenience methods
    signOut: async () => {
      await authClient.signOut();
      queryClient.setQueryData(BETTER_AUTH_SESSION_QUERY_KEY, null);
      await queryClient.invalidateQueries({ queryKey: GATEWAY_AUTH_QUERY_KEY });
      await refetch();
    },
  };
}

/**
 * Hook to require authentication
 * Throws error if user is not authenticated
 * Use in components that require authentication
 */
export function useRequireAuth() {
  const { user, isLoading } = useAuth();

  if (!isLoading && !user) {
    throw new Error("Authentication required");
  }

  return { user: user!, isLoading };
}
